/**
 * Production countdown timer — a purely LOCAL producer stopwatch. No socket, no
 * script, nothing reaches the presenter. Set minutes/seconds, press Start, and it
 * counts down; Start/Pause/Reset, and the boxes can be edited any time (live). The
 * display flashes under 30 seconds and holds at 00:00 when time is up.
 *
 * Replaces the phone-stopwatch we reach for at the top of every production.
 */
import { useEffect, useRef, useState } from 'react';

const clampNum = (v: string, max: number) => {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(max, n);
};

function fmt(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function ProducerTimer() {
  const [mins, setMins] = useState('5');
  const [secs, setSecs] = useState('00');
  const [running, setRunning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(5 * 60_000);
  const endAtRef = useRef(0);

  const durationFromInputs = () => clampNum(mins, 599) * 60_000 + clampNum(secs, 59) * 1000;

  // Tick while running; recompute purely from wall-clock so it never drifts.
  useEffect(() => {
    if (!running) return;
    const tick = () => {
      const left = Math.max(0, endAtRef.current - Date.now());
      setRemainingMs(left);
      if (left <= 0) setRunning(false);
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [running]);

  const start = () => {
    const total = remainingMs > 0 ? remainingMs : durationFromInputs();
    if (total <= 0) return;
    endAtRef.current = Date.now() + total;
    setRemainingMs(total);
    setRunning(true);
  };
  const pause = () => setRunning(false);
  const reset = () => {
    setRunning(false);
    setRemainingMs(durationFromInputs());
  };

  // Editing a box takes effect immediately — even mid-countdown.
  const editMins = (v: string) => {
    setMins(v);
    const total = clampNum(v, 599) * 60_000 + clampNum(secs, 59) * 1000;
    setRemainingMs(total);
    if (running) endAtRef.current = Date.now() + total;
  };
  const editSecs = (v: string) => {
    setSecs(v);
    const total = clampNum(mins, 599) * 60_000 + clampNum(v, 59) * 1000;
    setRemainingMs(total);
    if (running) endAtRef.current = Date.now() + total;
  };

  const done = remainingMs <= 0;
  const warn = running && remainingMs > 0 && remainingMs <= 30_000;

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>⏱ Production timer</h3>
        <span className="muted" style={{ fontSize: '0.75rem' }}>local only</span>
      </div>

      <div
        className={`prod-timer-display${warn ? ' prod-timer-warn' : ''}${done ? ' prod-timer-done' : ''}`}
        aria-live="off"
      >
        {done ? '00:00' : fmt(remainingMs)}
      </div>

      <div className="row" style={{ gap: '0.5rem', alignItems: 'flex-end', justifyContent: 'center' }}>
        <label className="stack" style={{ gap: '0.2rem', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: '0.72rem' }}>min</span>
          <input
            type="number"
            min="0"
            max="599"
            value={mins}
            onChange={(e) => editMins(e.target.value)}
            style={{ width: 64, textAlign: 'center' }}
            inputMode="numeric"
          />
        </label>
        <span style={{ fontSize: '1.4rem', paddingBottom: '0.2rem' }}>:</span>
        <label className="stack" style={{ gap: '0.2rem', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: '0.72rem' }}>sec</span>
          <input
            type="number"
            min="0"
            max="59"
            value={secs}
            onChange={(e) => editSecs(e.target.value)}
            style={{ width: 64, textAlign: 'center' }}
            inputMode="numeric"
          />
        </label>
      </div>

      <div className="row" style={{ gap: '0.5rem' }}>
        {running ? (
          <button className="danger" style={{ flex: 1 }} onClick={pause}>
            ⏸ Pause
          </button>
        ) : (
          <button className="primary" style={{ flex: 1 }} onClick={start} disabled={durationFromInputs() <= 0 && remainingMs <= 0}>
            ▶ Start
          </button>
        )}
        <button style={{ flex: 1 }} onClick={reset}>
          ↺ Reset
        </button>
      </div>
    </section>
  );
}
